import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlinePencilAlt } from "react-icons/hi";
import {
  IoSearch,
  IoEyeOutline,
  IoTrashOutline,
  IoFilter,
  IoClose,
  IoChevronDown,
  IoChevronUp,
} from "react-icons/io5";
import { IoMdArchive } from "react-icons/io";
import { MdOutlineWifiTetheringErrorRounded } from "react-icons/md";
import Search from "../../Jobick/Jobs/ui/search";
import { toast } from "react-toastify";
import { getItemsPerPage } from "../../../utils/CommonFunctions";
import UserService from "../../../../services/UserService";
import CategoryService from "../../../../services/CategoryService";
import { colors } from "../../../config/colors";

import DeleteModal from "../../shared/DeleteModal";

const ListEmployee = () => {
  const navigate = useNavigate();
  const [pageVal, setPageVal] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(1);
  const [employeeList, setEmployeeList] = useState(null);
  const [searchVal, setSearchVal] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showArchivedEmployees, setShowArchivedEmployees] = useState(false);
  const [archivingEmployee, setArchivingEmployee] = useState(null);
  const dropdownRef = useRef(null);
  const employeesPerPage = getItemsPerPage();
  const SEARCH_DEBOUNCE_DELAY = 500;

  const handlePreviousPageClick = () => {
    if (pageVal === 1) return;
    setPageVal(pageVal - 1);
  };

  const handleNextPageClick = () => {
    if (pageVal >= totalPages) return;
    setPageVal(pageVal + 1);
  };

  const handleDeleteClick = (employeeId) => {
    setEmployeeToDelete(employeeId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await UserService.deactivateUser(employeeToDelete);
      if (response.data.success) {
        toast.success("Employee deactivated successfully");
        fetchEmployeeList(); // Refresh the list
      } else {
        throw new Error("Failed to deactivate employee");
      }
    } catch (error) {
      console.error("Error deactivating employee:", error);
      toast.error("Failed to deactivate employee");
    } finally {
      setShowDeleteModal(false);
      setEmployeeToDelete(null);
    }
  };

  const fetchCategories = async () => {
    try {
      const categoryResponse = await CategoryService.getAllCategories(false);
      if (categoryResponse.success) {
        setCategories(categoryResponse.data || []);
      }
    } catch (error) {
      console.error("Error fetching skill sets:", error);
      toast.error("Failed to load skill sets");
    }
  };

  const handleFilterApply = () => {
    setPageVal(1);
    fetchEmployeeList(1, employeesPerPage, searchVal, selectedCategories);
    setShowFilterModal(false);
  };

  const handleClearFilter = () => {
    setSelectedCategories([]);
    setPageVal(1);
    fetchEmployeeList(1, employeesPerPage, searchVal, []);
  };

  const handleClearAllFilters = () => {
    setSearchVal("");
    setSelectedCategories([]);
    setPageVal(1);
    fetchEmployeeList(1, employeesPerPage, "", []);
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategories(prev => {
      const isSelected = prev.includes(categoryId);
      if (isSelected) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleArchiveEmployee = async (employeeId, isArchived = true) => {
    try {
      setArchivingEmployee(employeeId);
      const response = await UserService.archiveUser(employeeId, isArchived);
      if (response.data.success) {
        toast.success(isArchived ? "Employee archived successfully" : "Employee unarchived successfully");
        fetchEmployeeList();
      } else {
        throw new Error(response.data.error || `Failed to ${isArchived ? 'archive' : 'unarchive'} employee`);
      }
    } catch (error) {
      console.error(`Error ${isArchived ? 'archiving' : 'unarchiving'} employee:`, error);
      const errorMessage = error.response?.data?.error || error.message || `Failed to ${isArchived ? 'archive' : 'unarchive'} employee`;
      toast.error(errorMessage);
    } finally {
      setArchivingEmployee(null);
    }
  };

  const fetchEmployeeList = async (
    page = pageVal,
    limit = employeesPerPage,
    search = searchVal,
    categoryFilters = selectedCategories
  ) => {
    setLoading(1);
    try {
      const filters = {
        page,
        limit,
        search,
        role: 'EMPLOYEE',
        skills: categoryFilters,
        archived: showArchivedEmployees
      };

      const response = await UserService.getUsers(filters);

      if (response.data.status === "success") {
        const { data } = response.data;
        const totalUsers = data.totalCount;
        const totalPages = Math.ceil(totalUsers / limit);
        setEmployeeList(data.users);
        setTotalEmployees(totalUsers);
        setTotalPages(totalPages);
        setLoading(0);
      } else {
        throw new Error("Failed to fetch employees");
      }
    } catch (error) {
      setLoading(2);
      console.error("Error fetching employee list:", error);
      toast.error("Failed to fetch employees");
    }
  };

  useEffect(() => {
    fetchEmployeeList(pageVal, employeesPerPage, searchVal);
  }, [pageVal, showArchivedEmployees]);

  useEffect(() => {
    if (pageVal === 1) {
      fetchEmployeeList(1, employeesPerPage, searchVal);
    } else {
      setPageVal(1);
    }
  }, [searchVal, showArchivedEmployees]);

  useEffect(() => {
    fetchEmployeeList(pageVal, employeesPerPage, searchVal, selectedCategories);
  }, [selectedCategories]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleViewClick = (employeeId) => {
    navigate(`/view-employee/${employeeId}`);
  };

  const handleEditClick = (employeeId) => {
    navigate(`/edit-employee/${employeeId}`);
  };

  const handleCreateEmployee = () => {
    navigate("/add-employee");
  };

  const pgItem1 =
    pageVal === 1
      ? 1
      : pageVal >= totalPages && totalPages > 2
        ? totalPages - 2
        : pageVal - 1;
  const pgItem2 =
    pageVal === 1
      ? 2
      : pageVal >= totalPages && totalPages > 2
        ? totalPages - 1
        : pageVal;
  const pgItem3 =
    pageVal === 1
      ? 3
      : pageVal >= totalPages && totalPages > 2
        ? totalPages
        : pageVal + 1;

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchVal(value);
    }, SEARCH_DEBOUNCE_DELAY),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
  };

  function debounce(func, wait) {
    let timeout;
    const debouncedFunc = function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
    debouncedFunc.cancel = function () {
      clearTimeout(timeout);
    };
    return debouncedFunc;
  }

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCategories = categories?.filter(cat => cat.categoryType.toLowerCase().includes(categorySearchTerm.toLowerCase()));

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 p-6">
      {/* Header Section with Stats */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h2 className="text-2xl font-bold text-primary-headings mb-2">
              {showArchivedEmployees ? 'Archived Employees' : 'Employee Management'}
            </h2>
            <p className="text-sm text-primary-light mb-6">
              {showArchivedEmployees ? 'View and manage archived employees' : 'Track and manage your employees efficiently'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowArchivedEmployees(!showArchivedEmployees)}
              className={`inline-flex items-center px-4 py-2 rounded-md border transition-colors duration-200 shadow-sm whitespace-nowrap ${
                showArchivedEmployees 
                  ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <IoMdArchive className="h-4 w-4 mr-2" />
              <span className="text-sm">{showArchivedEmployees ? 'Show Active Employees' : 'Show Archived Employees'}</span>
            </button>
          </div>
        </div>

        {/* Search and Actions Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IoSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={inputValue}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent text-sm"
              placeholder={showArchivedEmployees ? "Search archived employees..." : "Search employees..."}
            />
          </div>
          {!showArchivedEmployees && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="inline-flex items-center px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200"
              >
                <IoFilter className="w-5 h-5 mr-2 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  Skill Sets {selectedCategories.length > 0 && `(${selectedCategories.length})`}
                </span>
                {showCategoryDropdown ? (
                  <IoChevronUp className="ml-2 w-4 h-4" />
                ) : (
                  <IoChevronDown className="ml-2 w-4 h-4" />
                )}
              </button>

              {showCategoryDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-3">
                    <div className="mb-2">
                      <input
                        type="text"
                        placeholder="Search skill sets..."
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light"
                        onChange={(e) => setCategorySearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {filteredCategories
                        .map(category => (
                          <div
                            key={category._id}
                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer rounded-md"
                            onClick={() => handleCategorySelect(category._id)}
                          >
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(category._id)}
                              onChange={() => {}}
                              className="h-4 w-4 text-primary-headings rounded border-gray-300"
                            />
                            <span className="ml-3 text-sm text-gray-700">
                              {category.categoryType}
                            </span>
                          </div>
                        ))
                      }
                      {filteredCategories.length === 0 && (
                        <div className="text-center text-gray-500 py-2">
                          No skill sets found
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="border-t p-3 bg-gray-50 rounded-b-lg">
                    <button
                      onClick={() => setSelectedCategories([])}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      Clear all
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {!showArchivedEmployees && (
            <button 
              onClick={handleCreateEmployee}
              className="inline-flex items-center px-6 py-2.5 text-white rounded-lg hover:bg-opacity-90 transition-all duration-200 shadow-sm font-medium whitespace-nowrap"
              style={{ backgroundColor: colors.highlights.callToAction }}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Employee
            </button>
          )}
        </div>
      </div>

      {/* Selected Filters Display */}
      <div className="pb-6">
        {selectedCategories.length > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mt-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Selected Skill Sets:</span>
              <div className="flex flex-wrap gap-2">
                {selectedCategories.slice(0, 5).map(categoryId => {
                  const category = categories.find(c => c._id === categoryId);
                  return category && (
                    <span
                      key={categoryId}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-accent-light text-accent-dark"
                    >
                      {category.categoryType}
                      <button
                        onClick={() => handleCategorySelect(categoryId)}
                        className="ml-2 hover:text-red-500"
                      >
                        <IoClose className="w-4 h-4" />
                      </button>
                    </span>
                  );
                })}
                
                {selectedCategories.length > 5 && (
                  <div className="relative">
                    <button
                      onClick={() => setShowMoreFilters(!showMoreFilters)}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      +{selectedCategories.length - 5} more
                      {showMoreFilters ? (
                        <IoChevronUp className="ml-1 w-4 h-4" />
                      ) : (
                        <IoChevronDown className="ml-1 w-4 h-4" />
                      )}
                    </button>
                    
                    {showMoreFilters && (
                      <div className="absolute top-full w-auto min-w-[200px] max-h-[300px] overflow-y-auto left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                        <div className="p-2">
                          {selectedCategories.slice(5).map(categoryId => {
                            const category = categories.find(c => c._id === categoryId);
                            return category && (
                              <div
                                key={categoryId}
                                className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 rounded-md gap-2"
                              >
                                <span className="text-sm text-gray-700">
                                  {category.categoryType}
                                </span>
                                <button
                                  onClick={() => handleCategorySelect(categoryId)}
                                  className="text-gray-400 hover:text-red-500"
                                >
                                  <IoClose className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {selectedCategories.length > 0 && (
                  <button
                    onClick={() => setSelectedCategories([])}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden max-h-[calc(100vh-480px)] flex flex-col mb-8">
        <div className="overflow-x-auto flex-1">
          <table className="w-full">
            <thead className="bg-white z-10 after:absolute after:inset-x-0 after:bottom-0 after:h-[1px] after:bg-gray-200">
              <tr>
                <th className="w-[60px] min-w-[60px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">No</th>
                <th className="w-full min-w-[200px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Name</th>
                <th className="w-[200px] min-w-[200px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Email</th>
                <th className="w-[150px] min-w-[150px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Phone</th>
                <th className="w-[120px] min-w-[120px] px-4 py-3 text-center text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading === 1 && (
                <tr>
                  <td colSpan="5" className="px-4 py-8">
                    <div className="flex items-center justify-center">
                      <div className="w-8 h-8 border-4 border-gray-300 rounded-full animate-spin" style={{ borderTopColor: colors.primary.headingsBody }}></div>
                    </div>
                  </td>
                </tr>
              )}

              {loading === 0 && employeeList?.map((employee, index) => (
                <tr 
                  key={employee._id} 
                  className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                  onClick={() => handleViewClick(employee._id)}
                >
                  <td className="w-[60px] min-w-[60px] px-4 py-3 text-sm font-medium text-primary-headings whitespace-nowrap">
                    {(pageVal - 1) * employeesPerPage + index + 1}
                  </td>
                  <td className="w-full min-w-[200px] px-4 py-3">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-primary-headings truncate">
                        {employee.name}
                        {showArchivedEmployees && (
                          <span className="text-xs text-yellow-600 ml-2 bg-yellow-50 px-2 py-1 rounded-full italic">Archived</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="w-[200px] min-w-[200px] px-4 py-3">
                    <span className="text-sm text-primary-light truncate block">
                      {employee.email}
                    </span>
                  </td>
                  <td className="w-[150px] min-w-[150px] px-4 py-3">
                    <span className="text-sm text-primary-light whitespace-nowrap">
                      {employee.phone}
                    </span>
                  </td>
                  <td className="w-[120px] min-w-[120px] px-4 py-3">
                    <div className="flex justify-center space-x-2 overflow-x-auto">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewClick(employee._id);
                        }}
                        className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors duration-200 shrink-0"
                      >
                        <IoEyeOutline size={18} className="text-blue-500" />
                      </button>
                      {!showArchivedEmployees && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(employee._id);
                          }}
                          className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors duration-200 shrink-0"
                        >
                          <HiOutlinePencilAlt size={18} className="text-indigo-500" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchiveEmployee(employee._id, !showArchivedEmployees);
                        }}
                        className="p-1.5 hover:bg-yellow-50 rounded-lg transition-colors duration-200 shrink-0"
                        title={showArchivedEmployees ? "Unarchive" : "Archive"}
                        disabled={archivingEmployee === employee._id}
                      >
                        {archivingEmployee === employee._id ? (
                          <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <IoMdArchive size={18} className="text-yellow-600" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {loading === 0 && (!employeeList || employeeList.length === 0) && (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <IoSearch size={48} className="text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No employees found</h3>
                      <p className="text-gray-500">Try adjusting your search or filters to find what you're looking for.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {loading === 2 && (
          <div className="flex flex-col items-center py-8">
            <MdOutlineWifiTetheringErrorRounded size={120} className="text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-800 mt-4">Error fetching data</h2>
            <h3 className="text-gray-500 mt-2">Check your network and try again</h3>
          </div>
        )}

        {/* Pagination Section */}
        {loading === 0 && employeeList?.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-100 bg-white">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-primary-light">
                Showing <span className="font-medium">{employeesPerPage * (pageVal - 1) + 1}</span> to{' '}
                <span className="font-medium">{employeesPerPage * (pageVal - 1) + employeeList.length}</span> of{' '}
                <span className="font-medium">{totalEmployees}</span> results
              </p>
              
              <nav className="flex items-center gap-2">
                <button
                  onClick={handlePreviousPageClick}
                  disabled={pageVal === 1}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50
                    ${pageVal === 1 ? 'bg-gray-100 text-primary-light' : 'bg-primary-headings text-white'}`}
                >
                  Previous
                </button>
                
                {totalPages > 0 && (
                  <>
                    <button
                      className={`px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200
                        ${pageVal === pgItem1 ? 'bg-primary-headings text-white' : 'bg-gray-100 text-primary-light'}`}
                      onClick={() => setPageVal(pgItem1)}
                    >
                      {pgItem1}
                    </button>
                    {totalPages > 1 && (
                      <button
                        className={`px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200
                          ${pageVal === pgItem2 ? 'bg-primary-headings text-white' : 'bg-gray-100 text-primary-light'}`}
                        onClick={() => setPageVal(pgItem2)}
                      >
                        {pgItem2}
                      </button>
                    )}
                    {totalPages > 2 && (
                      <button
                        className={`px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200
                          ${pageVal === pgItem3 ? 'bg-primary-headings text-white' : 'bg-gray-100 text-primary-light'}`}
                        onClick={() => setPageVal(pgItem3)}
                      >
                        {pgItem3}
                      </button>
                    )}
                  </>
                )}
                
                <button
                  onClick={handleNextPageClick}
                  disabled={pageVal >= totalPages}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50
                    ${pageVal >= totalPages ? 'bg-gray-100 text-primary-light' : 'bg-primary-headings text-white'}`}
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Delete Modal - Keep existing modal code */}
      {showDeleteModal && (
        <DeleteModal
          deleteItem="Employee"
          onDelete={handleConfirmDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setEmployeeToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default ListEmployee;
